'use client';

import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';

export type HelperTextType = 'info' | 'error';

interface InputTextProps extends InputHTMLAttributes<HTMLInputElement> {
  isLoading?: boolean;
  isError?: boolean;
  secondaryText?: string;
  helperText?: {
    type: HelperTextType;
    text: string;
  };
  endAdornment?: ReactNode;
}

export const InputText = forwardRef<HTMLInputElement, InputTextProps>(
  ({ isLoading, disabled, secondaryText, helperText, endAdornment, className, ...rest }, ref) => {
    const hasHelperText = !!helperText?.text;
    const hasSecondaryText = !!secondaryText;
    const isErrorState = hasHelperText && helperText?.type === 'error';

    const marginBottom = (hasHelperText ? 20 : 0) + (hasSecondaryText ? 20 : 0);

    return (
      <div className="relative flex w-full flex-col" style={{ marginBottom }}>
        {/* Input Container */}
        <div
          className={`flex h-11 rounded bg-[#f0f0f0] border ${
            disabled ? 'bg-[#d6d6d6]' : ''
          } ${isErrorState ? 'border-red-500' : 'border-transparent focus-within:border-[var(--color-team-brand-02)]'}`}
        >
          <input
            ref={ref}
            disabled={disabled || isLoading}
            className={`w-full max-w-[944px] rounded bg-transparent px-5 text-base leading-[44px] tracking-[-1px] outline-none disabled:text-[#7b7b7b] placeholder:text-[#b2b2b2] ${className}`}
            {...rest}
          />
          {endAdornment && <div className="flex h-full items-center pr-5">{endAdornment}</div>}
        </div>

        {/* Text Container */}
        {(hasHelperText || hasSecondaryText) && (
          <div
            className="absolute left-0"
            style={{ bottom: -((hasHelperText ? 19 : 0) + (hasSecondaryText ? 19 : 0)) }}
          >
            {secondaryText && (
              <p className="whitespace-nowrap pt-1 pl-2.5 text-left text-xs font-normal tracking-[-0.12px] text-[#7b7b7b]">
                {secondaryText}
              </p>
            )}
            {helperText?.text && (
              <p
                className={`whitespace-nowrap pt-1 pl-2.5 text-left text-xs font-normal tracking-[-0.12px] ${
                  helperText.type === 'error' ? 'text-red-500' : 'text-green-500'
                }`}
              >
                {helperText.text}
              </p>
            )}
          </div>
        )}
      </div>
    );
  }
);

InputText.displayName = 'InputText';
